import type { BaseResponse, Event, KnownPackageName, Package } from "@anggunpraya-templates/types";
import { createPreviewPackageDetail, usePreviewPackageMode } from "./use-preview-package-mode";

export interface PackageCapabilities {
  name: string;
  supportsDashboard: boolean;
  supportsRsvp: boolean;
  requiresGuestContext: boolean;
  supportsOpenTracking: boolean;
  supportsPublicPersonalization: boolean;
  isIntimate: boolean;
}

function normalizePackageName(name?: string | null) {
  return name?.trim().toLowerCase() || "";
}

const DEFAULT_PACKAGE_NAME: KnownPackageName = "Celebration";

/**
 * Menurunkan capability package menjadi policy runtime yang stabil.
 *
 * Resolver ini sengaja tidak bergantung penuh pada `package.name` agar:
 * - package masa depan tetap bisa bekerja tanpa refactor frontend besar
 * - payload lama yang belum lengkap tetap fallback ke behavior legacy
 */
export function resolvePackageCapabilities(pkg?: Partial<Package> | null): PackageCapabilities {
  const normalizedName = normalizePackageName(pkg?.name);
  const isIntimate = normalizedName === "intimate";

  const supportsDashboard = pkg?.include_user_dashboard ?? !isIntimate;
  const supportsRsvp = pkg?.include_rsvp ?? !isIntimate;
  const requiresGuestContext = supportsDashboard || supportsRsvp;

  return {
    supportsDashboard,
    supportsRsvp,
    requiresGuestContext,
    isIntimate,
    name: pkg?.name?.trim() || DEFAULT_PACKAGE_NAME,
    supportsOpenTracking: requiresGuestContext,
    supportsPublicPersonalization: !requiresGuestContext,
  };
}

/**
 * Composable shared untuk membaca capability package event aktif.
 *
 * Semua consumer baru sebaiknya membaca policy lewat composable ini,
 * bukan bercabang langsung dari `package.name`.
 */
export function usePackageCapabilities() {
  const { data: eventDetail } = useNuxtData<BaseResponse<Event>>("event-detail");
  const { isPreviewFlavor, previewPackageName } = usePreviewPackageMode();

  const packageDetail = computed(() => {
    const currentPackage = eventDetail.value?.data?.package ?? null;

    if (!isPreviewFlavor.value) {
      return currentPackage;
    }

    return createPreviewPackageDetail(previewPackageName.value, currentPackage);
  });
  const capabilities = computed(() => resolvePackageCapabilities(packageDetail.value));

  return {
    packageDetail,
    capabilities,
    packageName: computed(() => capabilities.value.name),
    supportsDashboard: computed(() => capabilities.value.supportsDashboard),
    supportsRsvp: computed(() => capabilities.value.supportsRsvp),
    requiresGuestContext: computed(() => capabilities.value.requiresGuestContext),
    supportsOpenTracking: computed(() => capabilities.value.supportsOpenTracking),
    supportsPublicPersonalization: computed(() => capabilities.value.supportsPublicPersonalization),
    isIntimate: computed(() => capabilities.value.isIntimate),
  };
}
